import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

// API Configuration
// Local: http://localhost:8000 | Production: https://api.cloudmortgage.io
const API_URL = 'http://localhost:8000';
const API_BASE = `${API_URL}/api/realtors`;
const SOURCE = 'rapidrates';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('realtor_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${API_BASE}/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.data?.realtor || data.data?.user || data.realtor || data.user);
      } else {
        // Token invalid, clear it
        localStorage.removeItem('realtor_token');
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source: SOURCE, email, password })
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error?.message || data.message || 'Login failed');
      error.code = data.error?.code;
      error.needsVerification = data.error?.code === 'EMAIL_NOT_VERIFIED';
      throw error;
    }

    const authToken = data.data?.token || data.token;
    const userData = data.data?.user || data.user;

    localStorage.setItem('realtor_token', authToken);
    setToken(authToken);
    setUser(userData);
    return data;
  };

  const register = async (userData) => {
    const response = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source: SOURCE, ...userData })
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error?.message || data.message || 'Registration failed');
      error.code = data.error?.code;
      error.details = data.error?.details;
      throw error;
    }

    return data;
  };

  const verifyEmail = async (verificationToken) => {
    const response = await fetch(`${API_BASE}/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source: SOURCE, token: verificationToken })
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error?.message || data.message || 'Verification failed');
      error.code = data.error?.code;
      throw error;
    }

    return data;
  };

  const resendVerification = async (email) => {
    const response = await fetch(`${API_BASE}/auth/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source: SOURCE, email })
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error?.message || data.message || 'Failed to resend verification');
      error.code = data.error?.code;
      throw error;
    }

    return data;
  };

  const forgotPassword = async (email) => {
    const response = await fetch(`${API_BASE}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source: SOURCE, email })
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error?.message || data.message || 'Failed to send reset email');
      error.code = data.error?.code;
      throw error;
    }

    return data;
  };

  const resetPassword = async (resetToken, newPassword) => {
    const response = await fetch(`${API_BASE}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source: SOURCE, token: resetToken, password: newPassword })
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error?.message || data.message || 'Password reset failed');
      error.code = data.error?.code;
      throw error;
    }

    return data;
  };

  const updateProfile = async (profileData) => {
    const response = await fetch(`${API_BASE}/me`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ source: SOURCE, ...profileData })
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error?.message || data.message || 'Profile update failed');
      error.code = data.error?.code;
      error.details = data.error?.details;
      throw error;
    }

    const userData = data.data?.realtor || data.data?.user || data.realtor || data.user;
    setUser(userData);
    return data;
  };

  const uploadPhoto = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/me/photo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error?.message || data.message || 'Photo upload failed');
      error.code = data.error?.code;
      throw error;
    }

    const photoUrl = data.data?.photo_url || data.photo_url;
    const thumbnailUrl = data.data?.thumbnail_url || data.thumbnail_url;
    setUser(prev => ({
      ...prev,
      photo: photoUrl,
      photo_thumbnail: thumbnailUrl
    }));
    return data;
  };

  const deletePhoto = async () => {
    const response = await fetch(`${API_BASE}/me/photo`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error?.message || data.message || 'Photo delete failed');
      error.code = data.error?.code;
      throw error;
    }

    setUser(prev => ({
      ...prev,
      photo: null,
      photo_thumbnail: null
    }));
    return data;
  };

  const logout = () => {
    localStorage.removeItem('realtor_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      verifyEmail,
      resendVerification,
      forgotPassword,
      resetPassword,
      updateProfile,
      uploadPhoto,
      deletePhoto,
      logout,
      isAuthenticated: !!user,
      source: SOURCE
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Public API functions for fetching realtors (no auth required)
export const fetchRealtors = async (params = {}) => {
  const { state, city, page = 1, limit = 20 } = params;
  const queryParams = new URLSearchParams({
    source: SOURCE,
    page: page.toString(),
    limit: limit.toString()
  });

  if (state) queryParams.append('state', state);
  if (city) queryParams.append('city', city);

  const response = await fetch(`${API_BASE}/directory?${queryParams}`);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error?.message || 'Failed to fetch realtors');
  }

  return data;
};

export const fetchRealtorBySlug = async (slug) => {
  const response = await fetch(`${API_BASE}/profile/${slug}`);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error?.message || 'Realtor not found');
  }

  return data;
};
